import React, { useState, useMemo, useCallback } from 'react'
import {
  Box, Button, Text, IcoMoon, InfoIcon
} from '@usap/uicomponents'
import OwnShippingLabel from './ShippingLabelInstructions/OwnShippingLabel'
import creditStatusMapping from '../../RMA/Mapping/creditStatusMapping'
import PrepaidShippingLabel from './ShippingLabelInstructions/PrepaidShippingLabel'
import returnRequestStatusMapping from "../../RMA/Mapping/returnRequestStatusMapping";

export default function ReturnStatus({
  orderDetails,
  isDesktop,
  creditStatus,
  returnRequestStatus,
  setShippingLabelLightboxInstruction,
  setDisplayLightbox,
  setDataChange,
}) {
  const {
    item,
  } = orderDetails

  const [displayTooltip, setDisplayTooltip] = useState(false)
  let messageData = creditStatusMapping(item.credit.statusCode, creditStatus)
  if (!item.credit.statusCode && item.returnRequestStatus) {
    messageData = returnRequestStatusMapping(item.returnRequestStatus, returnRequestStatus)
  }
  const hasShippingLabel = !!((item.shipping && item.shipping.label) || item.credit.statusCode === '3005')

  const idTypeDisplay = useMemo(() => {
    const { rmaId, returnRequestId, creditId } = item

    if (rmaId && rmaId !== 'GP RMA not received.') {
      return (
        <>
          <Text size="small" color="dark-2">RMA ID: &nbsp;</Text>
          <Text size="small" color="dark-2" weight="bold">{ rmaId }</Text>
        </>
      )
    }

    if (creditId) {
      return (
        <>
          <Text size="small" color="dark-2">Credit ID: &nbsp;</Text>
          <Text size="small" color="dark-2" weight="bold">{ creditId }</Text>
        </>
      )
    }

    return (
      <>
        <Text size="small" color="dark-2">Return Request ID: &nbsp;</Text>
        <Text size="small" color="dark-2" weight="bold">{ returnRequestId }</Text>
      </>
    )
  }, [item])

  const openShippingLabelDownloadLink = useCallback(() => {
    const { shipping } = item
    window.open(shipping.label)
  }, [item])

  const displayShippingLabelDownloadButton = useMemo(() => {
    const { shipping } = item
    if (!shipping) {
      return null
    }
    if (typeof shipping.label !== 'undefined') {
      const d = new Date(shipping.expiryDate)
      const options = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }
      const expiryDate = d.toLocaleDateString('en-US', options)
      return (
        <Box direction="column" align="center">
          <Box align="center" width="100%">
            <Button
              id="ReturnDetails-DownloadShippingLabel"
              primary
              type="submit"
              onClick={openShippingLabelDownloadLink}
              custom
              hideBorder
              size="large"
              label="Download Shipping Label"
              style={{
                width: '100%',
                fontFamily: 'Roboto',
                fontSize: 16,
                letterSpacing: 0,
                textAlign: 'center',
                lineHeight: '40px',
              }}
            />
          </Box>
          <Box align="center" direction="row" margin={{ left: isDesktop ? '5px' : '0', top: isDesktop ? '0' : '4px' }}>
            <IcoMoon icon={InfoIcon} size="small" color="#4A90E2" />
            <Text size="xsmall" margin={{ left: '5px' }}>
              Please ship your part(s) by
              &nbsp;
              <Text size="xsmall" weight="bold">{ expiryDate }</Text>
              &nbsp;
              to avoid return cancellation
            </Text>
          </Box>
        </Box>
      )
    }

    return null
  }, [item])

  const displayReturnAddress = useMemo(() => {
    const { returnAddress, credit, shipping } = item
    if (returnAddress && credit.statusCode === '3005' && !shipping) {
      const {
        address,
        city,
        state,
        zip,
      } = returnAddress
      const addressDisplay = `${address}, ${city}, ${state}, ${zip}`
      return (
        <>
          <Text size="small" color="dark-2">
            Return Address:
            <br />
          </Text>
          <Text size="small" weight="bold" color="dark-2">
            { addressDisplay }
          </Text>
        </>
      )
    }

    return null
  }, [item])

  const triggerLightboxShippingLabelInstruction = () => {
    const { returnAddress, shipping } = item
    if (!returnAddress) {
      return false
    }
    const {
      address,
      city,
      state,
      zip,
    } = returnAddress
    const addressDisplay = `${address}, ${city}, ${state}, ${zip}`
    if (hasShippingLabel && shipping && shipping.label) {
      setShippingLabelLightboxInstruction({ id: 'prepaid-shipping-label' })
      setDisplayLightbox(true)
      setDataChange(Math.random())
    } else {
      setShippingLabelLightboxInstruction({ id: 'own-shipping-label', returnAddress: addressDisplay })
      setDisplayLightbox(true)
      setDataChange(Math.random())
    }

    return true
  }

  const mobileShippingLabelInstruction = useMemo(() => {
    const { credit, shipping = {} } = item
    if (!isDesktop && (credit.statusCode === '3005' || shipping.label)) {
      return (
        <Box margin={{ top: '16px' }} justify={isDesktop ? 'start' : 'center'} align={isDesktop ? 'start' : 'center'}>
          <Text
            onClick={triggerLightboxShippingLabelInstruction}
            size="small"
            weight="bold"
            color="#05629B"
            style={{ textDecoration: 'underline' }}
          >
            See Return Package Instructions
          </Text>
        </Box>
      )
    }

    return null
  }, [item, isDesktop])

  const displayShippingLabelInstruction = useMemo(() => {
    const { credit, shipping, returnAddress } = item
    if (shipping && shipping.label) {
      return (
        <Box width="50%" pad="medium">
          <PrepaidShippingLabel isDesktop={isDesktop} />
        </Box>
      )
    }
    if ((credit.statusCode === '3005' || credit.statusCode === '3010') && returnAddress) {
      const {
        address,
        city,
        state,
        zip,
      } = returnAddress
      const addressDisplay = `${address}, ${city}, ${state}, ${zip}`
      return (
        <Box width="50%" pad="medium">
          <OwnShippingLabel addressDisplay={addressDisplay} isDesktop={isDesktop} />
        </Box>
      )
    }

    return null
  }, [item])

  const toggleTooltipRefund = () => {
    if (displayTooltip) {
      setDisplayTooltip(false)
      return true
    }
    setDisplayTooltip(true)
    return true
  }

  const displayPricing = useMemo(() => {
    const { credit } = item
    const displayCallTagFee = credit && credit.callTagFee > 0 ? 'flex' : 'none'
    const displayTotalRefund = credit && credit.total > 0 ? 'flex' : 'none'
    return (
      <Box margin={{ bottom: '12px' }}>
        {
          (credit && credit.callTagFee > 0 && (
            <Box direction="row" style={{ display: displayCallTagFee }}>
              <Text size="small" color="dark-2">Return Shipping: &nbsp;</Text>
              <Text size="small" color="#DB2429" weight="bold">
                - $
                { credit.callTagFee.toFixed(2) }
              </Text>
            </Box>
          ))
        }
        {
          (credit && credit.total > 0 && (
            <Box direction="row" style={{ display: displayTotalRefund }}>
              <Text size="small" color="dark-2" weight="bold">
                Refund: &nbsp;
              </Text>
              <Text size="small" color="dark-2" weight="bold">
                $
                { credit.total.toFixed(2) }
              </Text>
            </Box>
          ))
        }
      </Box>
    )
  }, [item])

  const determineSubReason = useMemo(() => {
    const { shipping, credit } = item
    if (credit.statusCode === '3005') {
      if (shipping && shipping.label) {
        return (
          <Text size="small" color="dark-2">
            { messageData.sub_reason.shipping_label }
          </Text>
        )
      }
      return (
        <Text size="small" color="dark-2">
          { messageData.sub_reason.no_shipping_label }
        </Text>
      )
    }
    return (
      <Text size="small" color="dark-2">
        { messageData && messageData.sub_reason }
      </Text>
    )
  }, [item])

  return (
    <Box
      margin={{ top: '16px' }}
      round="small"
      border={{
        color: '#CCCACA',
        size: 'small',
        side: 'all',
      }}
      direction="row"
    >
      <Box
        width={hasShippingLabel && isDesktop ? '50%' : '100%'}
        pad="medium"
        style={{
          borderRight: hasShippingLabel ? '1px solid #E0E0E0' : 'none',
        }}
      >
        <Box>
          <Text size="16px" color="dark-2" weight="bold">{messageData && messageData.main_reason }</Text>
        </Box>
        <Box margin={{ bottom: '12px' }}>
          { determineSubReason }
        </Box>
        <Box margin={{ bottom: '12px' }}>
          <Box direction="row">
            { idTypeDisplay }
          </Box>
          { displayPricing }
        </Box>
        { displayShippingLabelDownloadButton }
        { displayReturnAddress }
        { mobileShippingLabelInstruction }
      </Box>
      {
        isDesktop && displayShippingLabelInstruction
      }
    </Box>
  )
}
